import { useControlledState } from '@react-stately/utils';
import clsx from 'clsx';
import * as React from 'react';

import { useLiveRef } from '@gitbook/util-react-hooks';

import { AutoSizeInput, type AutoSizeInputProps } from './AutoSizeInput';
import { AutoSizeTextarea, type AutoSizeTextareaProps } from './AutoSizeTextarea';
import { toast } from './Toaster';

export type InlineEditState = 'pending' | 'saving';

type AbstractInlineEditProps<T extends HTMLElement> = {
    value: string;
    placeholder?: string;
    readOnly?: boolean;
    disabled?: boolean;
    className?: string;
    transformValue?: (value: string) => string;
    onSave: (value: string, api: { reset: () => void }) => void | Promise<void>;
    state?: InlineEditState;
    onStateChange?: (state: InlineEditState) => void;
    renderControl: (props: {
        className: string;
        controlClassName: string;
        'aria-busy': React.AriaAttributes['aria-busy'];
        value: string;
        autoFocus?: boolean;
        readOnly?: boolean;
        placeholder?: string;
        onBlur: React.FocusEventHandler<T>;
        onChange: React.ChangeEventHandler<T>;
        onKeyDown: React.KeyboardEventHandler<T>;
    }) => React.ReactElement;
    autoFocus?: boolean;
    'data-testid'?: string;
};

/**
 * Abstract inline edit component.
 */
function AbstractInlineEdit(
    props: AbstractInlineEditProps<HTMLInputElement | HTMLTextAreaElement>
) {
    const { onSave, value: propValue, className, placeholder, readOnly, renderControl } = props;
    const [state, setState] = useControlledState<InlineEditState>(
        props.state,
        'pending',
        props.onStateChange
    );
    const saving = state === 'saving';
    // Display shimmer effect after 400ms of saving
    const [value, setValue] = React.useState(propValue);
    const valueRef = useLiveRef(value);

    React.useLayoutEffect(() => {
        if (propValue !== valueRef.current) {
            setValue(propValue);
        }
    }, [propValue, valueRef]);

    async function save() {
        if (value === propValue) {
            setState('pending');
            return;
        }

        setState('saving');

        await toast
            .catch(async () => {
                try {
                    await onSave(value, { reset: () => setValue(propValue) });
                } catch (error) {
                    setValue(propValue);
                    throw error;
                }
            }, 'Error while saving value')
            .finally(() => {
                setState('pending');
            });
    }

    if (readOnly) {
        return (
            <div
                data-testid={props['data-testid']}
                className={clsx(!value && 'text-muted', className)}
            >
                {value || placeholder}
            </div>
        );
    }

    return renderControl({
        className: clsx(saving ? 'animate-pulse' : null, className),
        controlClassName: 'placeholder:text-muted',
        'aria-busy': state === 'saving',
        value,
        readOnly: state === 'saving',
        placeholder,
        autoFocus: props.autoFocus,
        onBlur: (event) => {
            if (event.isDefaultPrevented()) {
                return;
            }

            save();
        },
        onChange: (event) => {
            if (event.isDefaultPrevented()) {
                return;
            }

            const { value } = event.target;
            setValue(props.transformValue ? props.transformValue(value) : value);
        },
        onKeyDown: (event) => {
            if (event.isDefaultPrevented()) {
                return;
            }

            switch (event.key) {
                case 'Escape':
                    event.stopPropagation();
                    setValue(propValue);
                    setState('pending');
                    break;
                case 'Enter':
                    event.preventDefault();
                    event.stopPropagation();
                    event.currentTarget.blur();
                    break;
            }
        },
    });
}

export type InlineInputEditProps = Omit<
    AbstractInlineEditProps<HTMLInputElement>,
    'renderControl'
> &
    Omit<AutoSizeInputProps, 'value' | 'onChange'> &
    Partial<Pick<AutoSizeInputProps, 'onChange'>> & {
        kind?: 'default' | 'highlighted';
    };

/**
 * Inline edit single line text input.
 */
export const InlineInputEdit = React.forwardRef<HTMLInputElement, InlineInputEditProps>(
    function InlineInputEdit(props, ref) {
        const {
            kind = 'default',
            onSave,
            transformValue,
            value,
            placeholder,
            readOnly,
            disabled,
            className,
            state,
            onStateChange,
            autoFocus,
            ...inputProps
        } = props;
        return (
            <AbstractInlineEdit
                onSave={onSave}
                transformValue={transformValue}
                value={value}
                placeholder={placeholder}
                readOnly={readOnly}
                disabled={disabled}
                className={clsx(
                    'truncate',
                    kind === 'highlighted' && !readOnly
                        ? 'rounded border border-transparent focus-within:border-primary hover:border-base focus-within:hover:border-primary'
                        : null,
                    className
                )}
                state={state}
                onStateChange={onStateChange}
                autoFocus={autoFocus}
                renderControl={({ onBlur, onChange, onKeyDown, controlClassName, ...rest }) => {
                    return (
                        <AutoSizeInput
                            ref={ref}
                            {...inputProps}
                            {...rest}
                            onBlur={(event) => {
                                props.onBlur?.(event);
                                onBlur(event);
                            }}
                            onChange={(event) => {
                                props.onChange?.(event);
                                onChange(event);
                            }}
                            onKeyDown={(event) => {
                                props.onKeyDown?.(event);
                                onKeyDown(event);
                            }}
                            inputClassName={controlClassName}
                        />
                    );
                }}
            />
        );
    }
);

export type InlineTextareaEditProps = Omit<
    AbstractInlineEditProps<HTMLTextAreaElement>,
    'renderControl'
> &
    Omit<AutoSizeTextareaProps, 'value' | 'onChange'> &
    Partial<Pick<AutoSizeTextareaProps, 'onChange'>>;

/**
 * Inline edit multi line text input.
 */
export const InlineTextareaEdit = React.forwardRef<HTMLTextAreaElement, InlineTextareaEditProps>(
    function InlineTextareaEdit(props, ref) {
        const {
            onSave,
            onSubmit,
            transformValue,
            value,
            placeholder,
            readOnly,
            disabled,
            className,
            state,
            onStateChange,
            autoFocus,
            ...textareaProps
        } = props;
        return (
            <AbstractInlineEdit
                value={value}
                placeholder={placeholder}
                readOnly={readOnly}
                disabled={disabled}
                className={className}
                state={state}
                onStateChange={onStateChange}
                autoFocus={autoFocus}
                onSave={onSave}
                transformValue={transformValue}
                renderControl={({ onBlur, onChange, onKeyDown, controlClassName, ...rest }) => {
                    return (
                        <AutoSizeTextarea
                            ref={ref}
                            {...textareaProps}
                            {...rest}
                            onBlur={(event) => {
                                props.onBlur?.(event);
                                onBlur(event);
                            }}
                            onChange={(event) => {
                                props.onChange?.(event);
                                onChange(event);
                            }}
                            onKeyDown={(event) => {
                                props.onKeyDown?.(event);
                                // Allow new lines with shift + enter
                                if (event.key === 'Enter' && event.shiftKey) {
                                    return;
                                }
                                onKeyDown(event);
                            }}
                            textareaClassName={clsx(props.textareaClassName, controlClassName)}
                        />
                    );
                }}
            />
        );
    }
);
